import { type MutableRefObject, useEffect, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { ff } from '@atlassian/jira-feature-flagging';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { CARD_LIST_DROPPABLE_TYPE } from '../../../common/constants/index.tsx';
import { CARD_DRAGGABLE_ID_PREFIX } from '../../../services/card-list-dnd/index.tsx';
import { dragEnd } from '../../../state/actions/drag/index.tsx';
import { useBacklogDispatch } from '../../../state/index.tsx';
import { useIsCMPKanbanBacklog } from '../../../state/state-hooks/capabilities.tsx';
import { useSimplyMoveCards } from '../../../state/state-hooks/use-simply-move-cards/index.tsx';

interface CardListHeaderDropTargetParams {
	headerElementRef: MutableRefObject<HTMLDivElement | null>;
	cardListId: string;
	isHeaderDropDisabled?: boolean;
}

function useCardListHeaderDropTarget({
	headerElementRef,
	cardListId,
	isHeaderDropDisabled,
}: CardListHeaderDropTargetParams): { isDragOver: boolean } {
	const autobatch = useAutobatch();
	const moveCards = useSimplyMoveCards();
	const [isDragOver, setIsDragOver] = useState(false);
	const isCompanyManagedKanban = useIsCMPKanbanBacklog();

	useEffect(() => {
		if (!headerElementRef.current) return undefined;

		return dropTargetForElements({
			element: headerElementRef.current,
			getData: () => ({
				type: CARD_LIST_DROPPABLE_TYPE,
			}),
			canDrop: (args) =>
				!isHeaderDropDisabled &&
				args.source.data.type === CARD_DRAGGABLE_ID_PREFIX &&
				(!args.source.data.cardIsSubtask || !!isCompanyManagedKanban),
			onDrop: (args) => {
				autobatch(() => {
					setIsDragOver(false);
					if (args.source.data.type === CARD_DRAGGABLE_ID_PREFIX) {
						moveCards({
							source: { cardId: Number(args.source.data.cardId) },
							location: { type: 'list', targetListId: cardListId },
						});
					}
				});
			},
			onDragEnter: () => setIsDragOver(true),
			onDragLeave: () => setIsDragOver(false),
		});
	}, [
		autobatch,
		cardListId,
		headerElementRef,
		isHeaderDropDisabled,
		moveCards,
		isCompanyManagedKanban,
	]);

	return {
		isDragOver,
	};
}

function useTransitionZoneDropTarget({
	cardListId,
	transitionZoneElementRef,
}: {
	cardListId: string;
	transitionZoneElementRef: MutableRefObject<HTMLDivElement | null>;
}) {
	const autobatch = useAutobatch();

	const moveCards = useSimplyMoveCards();

	const dispatch = useBacklogDispatch();

	const [isDragOver, setIsDragOver] = useState(false);

	useEffect(() => {
		if (!transitionZoneElementRef.current) return undefined;

		return dropTargetForElements({
			element: transitionZoneElementRef.current,
			onDragEnter: fg('tnk-2613-hover-dropzones') ? () => setIsDragOver(true) : undefined,
			onDragLeave: fg('tnk-2613-hover-dropzones') ? () => setIsDragOver(false) : undefined,

			canDrop: (args) => args.source.data.type === CARD_DRAGGABLE_ID_PREFIX,
			onDrop: (args) => {
				autobatch(() => {
					if (fg('tnk-2613-hover-dropzones')) {
						setIsDragOver(false);
					}
					if (args.source.data.type === CARD_DRAGGABLE_ID_PREFIX) {
						moveCards({
							source: { cardId: Number(args.source.data.cardId) },
							location: { type: 'list', targetListId: cardListId },
						});
					}

					if (ff('fix-hanging-low-opacity-after-backlog-card-drag-and-drop_0roex')) {
						dispatch(dragEnd());
					}
				});
			},
		});
	}, [transitionZoneElementRef, moveCards, cardListId, autobatch, dispatch]);
	return { isDragOver };
}

type CardListDropTargetParams = CardListHeaderDropTargetParams & {
	transitionZoneElementRef: MutableRefObject<HTMLDivElement | null>;
};

export function useCardListDropTarget({
	headerElementRef,
	transitionZoneElementRef,
	cardListId,
	isHeaderDropDisabled,
}: CardListDropTargetParams) {
	const { isDragOver } = useCardListHeaderDropTarget({
		headerElementRef,
		cardListId,
		isHeaderDropDisabled,
	});

	const { isDragOver: isDragOverTransitionZone } = useTransitionZoneDropTarget({
		cardListId,
		transitionZoneElementRef,
	});

	return {
		isDragOver,
		isDragOverTransitionZone,
	};
}
